import { useRef } from "react";
import { Link, useNavigate } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { NewsletterForm } from "@/components/NewsletterForm";
import { GuideCollection } from "@/components/GuideBlock";
import { IMAGES, CONTACT, THRESHOLD_IMAGES, JOURNAL_IMAGES, useContent } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

const Arrival = () => {
  const { ui } = useContent();
  const h = ui.home;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="hero-arrival">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src={IMAGES.hero} alt="A woman in late-afternoon light among plants" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/35" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-ink/60 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-24 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.4 }}
          className="mb-8 text-xs tracking-[0.35em] uppercase text-cream/80"
          data-testid="hero-eyebrow"
        >
          {CONTACT.area}
        </motion.p>
        <h1 className="font-serif text-cream leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-[5.5rem]" data-testid="hero-headline">
          {h.heroLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "italic text-sand" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-cream"
        >
          {h.heroIntro}
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-cream/70"
        data-testid="hero-scroll-hint"
      >
        <span className="text-xs tracking-[0.3em] uppercase">{h.scrollHint}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }} className="block h-8 w-px bg-cream/60" />
      </motion.div>
    </section>
  );
};

const Recognition = () => {
  const { ui } = useContent();
  const r = ui.home.recognition;
  return (
    <section className="bg-cream py-28 lg:py-36" data-testid="section-recognition">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ink">
              {r.titleA} <span className="italic text-clay">{r.titleAccent}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:pt-24">
            <p className="text-base leading-relaxed text-soft">{r.p1}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Understanding = () => {
  const { ui, thresholds } = useContent();
  const u2 = ui.home.understanding;
  return (
    <section className="bg-parchment py-28 lg:py-36" data-testid="section-understanding">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink max-w-3xl">
            {u2.title}
          </h2>
        </Reveal>

        <div className="mt-20 space-y-24 lg:space-y-32">
          {thresholds.map((t, i) => (
            <Reveal key={t.slug} delay={0.05}>
              <div className="grid items-center gap-10 lg:grid-cols-12" data-testid={`threshold-card-${t.slug}`}>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""} relative`}>
                  <div className="overflow-hidden rounded-t-[10rem]">
                    <motion.img
                      src={THRESHOLD_IMAGES[t.slug]}
                      alt={t.name}
                      className="aspect-[4/5] w-full object-cover"
                      whileHover={{ scale: 1.04 }}
                      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                  <p className="font-serif italic text-lg text-clay mb-3">{t.subtitle}</p>
                  <h3 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{t.name}</h3>
                  <p className="mt-5 text-base leading-relaxed text-soft">{t.short}</p>
                  <Link
                    to={`/thresholds/${t.slug}`}
                    data-testid={`threshold-link-${t.slug}`}
                    className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust"
                  >
                    {u2.enter}
                    <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Upcoming = () => {
  const { ui } = useContent();
  const u = ui.home.upcoming;
  return (
    <section className="bg-cream py-28 lg:py-36" data-testid="section-upcoming">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink max-w-3xl">{u.title}</h2>
          <p className="mt-4 text-sm leading-relaxed text-soft max-w-xl">{u.sub}</p>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden border border-ink/10 bg-ink/10 md:grid-cols-3">
          {u.cards.map((card, i) => (
            <Reveal key={card.testid} delay={i * 0.1} className="h-full">
              <Link
                to={card.to}
                data-testid={card.testid}
                className="group flex h-full flex-col justify-between bg-cream p-10 transition-colors duration-500 hover:bg-ink"
              >
                <div>
                  <p className="font-serif text-2xl leading-snug text-ink group-hover:text-cream transition-colors duration-500">{card.name}</p>
                  <p className="mt-4 text-sm font-medium tracking-wide text-clay group-hover:text-sand transition-colors duration-500">{card.date}</p>
                  <p className="mt-1 text-xs tracking-[0.2em] uppercase text-soft group-hover:text-cream/60 transition-colors duration-500">{card.time} · {card.location} · {card.price}</p>
                  <p className="mt-4 text-sm leading-relaxed text-soft group-hover:text-cream/70 transition-colors duration-500">{card.desc}</p>
                </div>
                <span className="mt-10 inline-flex items-center gap-2 text-sm text-clay group-hover:text-sand transition-colors duration-500">
                  {u.cta} <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ——— 5b. WOMEN'S CIRCLE INVITATION ——— */
const WomensCircleInvite = () => {
  const { ui } = useContent();
  const wc = ui.home.circle;
  return (
    <section className="bg-cream border-b border-ink/10 py-20 lg:py-24" data-testid="section-womens-circle">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="overflow-hidden rounded-t-[8rem] max-w-xs">
            <img loading="lazy" src="/images/circle.jpg" alt="A small circle of women gathered on cushions" className="w-full aspect-[4/5] object-cover" data-testid="circle-image" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{wc.title}</h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-soft">{wc.p1} {wc.p2}</p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-soft">{wc.p3}</p>
          <p className="mt-6 text-sm tracking-wide text-ink/70" data-testid="circle-facts">
            {wc.facts} · <span className="italic font-serif text-clay">{wc.next}</span>
          </p>
          <Link to="/womens-circle" data-testid="circle-cta"
            className="group mt-7 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
            {wc.cta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Trust = () => {
  const { ui } = useContent();
  const t = ui.home.trust;
  return (
    <section className="bg-parchment py-28 lg:py-36" data-testid="section-trust">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src={IMAGES.irida} alt="Irida, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="irida-portrait" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <p className="font-serif italic text-xl text-clay">{t.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-ink leading-snug">
              {t.quote} <span className="italic">{t.quoteAccent}</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{t.body}</p>
            <Link
              to="/about"
              data-testid="trust-about-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust"
            >
              {t.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Exploration = () => {
  const { ui, journal } = useContent();
  const e = ui.home.exploration;
  return (
    <section className="bg-cream py-28 lg:py-36" data-testid="section-exploration">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">
              {e.title} <span className="italic text-clay">{e.accent}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/journal" data-testid="journal-all-link" className="group inline-flex items-center gap-2 text-sm text-clay hover:text-rust transition-colors duration-300">
              {ui.common.allEntries} <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {journal
            .slice(0, 3)
            .map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1} className="h-full">
              <Link to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="group block h-full">
                <div className="overflow-hidden">
                  <motion.img
                    src={JOURNAL_IMAGES[p.slug]}
                    alt={p.title}
                    className="aspect-[4/3] w-full object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
                <p className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">{p.threshold} · {p.readTime}</p>
                <h3 className="mt-3 font-serif text-2xl leading-snug text-ink group-hover:text-clay transition-colors duration-300">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-soft line-clamp-3">{p.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stillness = () => {
  const { ui } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative overflow-hidden py-40 lg:py-56" data-testid="section-stillness">
      <motion.div style={{ y }} className="absolute inset-0 -top-24 -bottom-24">
        <img loading="lazy" src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/55" />
      </motion.div>
      <Reveal className="relative z-10 mx-auto max-w-3xl px-6 text-center">
        <p className="font-serif italic text-3xl sm:text-4xl lg:text-[2.75rem] leading-snug text-cream">
          {ui.home.stillness.quoteLines.map((l, i) => (
            <span key={i}>{l}{i < ui.home.stillness.quoteLines.length - 1 && <br />}</span>
          ))}
        </p>
      </Reveal>
    </section>
  );
};

const Connection = () => {
  const { ui } = useContent();
  const c = ui.home.connection;
  const navigate = useNavigate();
  return (
    <section id="connection" className="bg-parchment py-28 lg:py-36" data-testid="section-connection">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight">
              {c.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-soft">{c.body}</p>
            <div className="mt-8"><NewsletterForm /></div>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="connection-instagram"
              className="group mt-7 inline-flex items-center gap-2.5 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust"
            >
              <Instagram size={15} strokeWidth={1.5} />
              {c.instagram}
              <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-col justify-center">
            <p className="font-serif italic text-2xl leading-snug text-ink">
              {c.alt1}
            </p>
            <div className="mt-8">
              <button
                onClick={() => navigate("/contact")}
                data-testid="connection-contact-button"
                className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
              >
                {c.cta}
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { ui } = useContent();
  useSeo({ ...ui.seo.home, image: `/irida.jpg` });
  return (
    <main data-testid="home-page">
      <Arrival />
      <Recognition />
      <Understanding />
      <EditorialMarquee />
      <Upcoming />
      <Trust />
      <WomensCircleInvite />
      <GuideCollection />
      <Exploration />
      <Stillness />
      <Connection />
    </main>
  );
}
